import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AppLayout from "@/pages/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Findings from "@/pages/Findings";
import Playbooks from "@/pages/Playbooks";
import Reports from "@/pages/Reports";
import AuditTrail from "@/pages/AuditTrail";
import Admin from "@/pages/Admin";
import Settings from "@/pages/Settings";
import AIReview from "@/pages/AIReview";
import AuthCallback from "@/pages/AuthCallback";
import Pricing from "@/pages/Pricing";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Platform from "@/pages/Platform";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Home from "@/pages/Home";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ConnectionBanner } from "@/components/ConnectionBanner";

function Private({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="p-10 text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// "/" is the public homepage for visitors and the app for signed-in users; deep links keep redirecting to /login.
function RootGate({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <div className="p-10 text-slate-400">Loading…</div>;
  if (!user) return location.pathname === "/" ? <Home /> : <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Emergent Google Auth returns to {origin}/#session_id=... — handle before any protected route
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<Private><PaymentSuccess /></Private>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route
        path="/"
        element={
          <RootGate>
            <AppLayout />
          </RootGate>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="projects/:id/review" element={<AIReview />} />
        <Route path="projects/:id/findings" element={<Findings />} />
        <Route path="projects/:id/report" element={<Reports />} />
        <Route path="playbooks" element={<Playbooks />} />
        <Route path="audit" element={<AuditTrail />} />
        <Route path="admin" element={<Admin />} />
        <Route path="settings" element={<Settings />} />
        <Route path="billing" element={<Billing />} />
        <Route path="platform" element={<Platform />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrandProvider>
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" />
        <ConnectionBanner />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
    </BrandProvider>
    </ErrorBoundary>
  );
}
